import { useState } from "react";

import { Button, Group, Modal, Stack, Text } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";

import { ProcedureCode } from "../types/fee-table";

interface CustomCodeDeleteModalProps {
  opened: boolean;
  onClose: () => void;
  procedure: ProcedureCode | null;
  onConfirm: () => Promise<void>;
}

export default function CustomCodeDeleteModal({
  opened,
  onClose,
  procedure,
  onConfirm,
}: CustomCodeDeleteModalProps) {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsDeleting(true);
      await onConfirm();
      onClose();
    } catch (error) {
      // Error is already handled by onConfirm, just don't close the modal
      console.error("Error deleting custom code:", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <Modal
      centered
      opened={opened}
      onClose={onClose}
      title={
        <TextWrapper fz="lg" c="dark.9" variant="bold">
          Delete Custom Code
        </TextWrapper>
      }
    >
      <Stack gap="md" data-testid="custom-code-delete-modal">
        <Text>
          Are you sure you want to delete the custom code <strong>{procedure?.code}</strong> -{" "}
          <strong>{procedure?.name}</strong>? This action cannot be undone.
        </Text>
        <Group gap="sm" justify="flex-end">
          <Button
            variant="subtle"
            onClick={onClose}
            disabled={isDeleting}
            data-testid="custom-code-delete-modal-cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="red"
            loading={isDeleting}
            disabled={isDeleting}
            onClick={handleConfirm}
            data-testid="custom-code-delete-modal-confirm-button"
          >
            Delete
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
